import React from 'react';
import { KENYA_COUNTY_PATHS } from './kenyaCountyPaths';

export interface CountyData {
  name: string;
  lat: number;
  lng: number;
  registered: number;
  unregistered: number;
  percentage: number;
}

interface KenyaMapProps {
  countyData?: CountyData[];
  onCountyClick?: (county: CountyData) => void;
  /** The currently-selected county (matches the dashboard's COUNTY filter) —
   *  highlights that county's real shape and dims the rest, mirroring how the
   *  filter dropdown drives every other panel on the page. */
  selectedCounty?: string;
  /** Map background and county outline (default dark). */
  background?: string;
  /** Legend text colour. */
  labelColor?: string;
}

function pct2color(p: number) {
  if (p >= 80) return '#00D084';
  if (p >= 60) return '#FFA500';
  if (p >= 40) return '#FF6B4A';
  if (p > 0) return '#C41E3A';
  return '#E8E6DE'; // no data yet — light neutral, not dark-on-dark invisible
}

const VIEW_W = 480;
const VIEW_H = 520;

export const KenyaMap: React.FC<KenyaMapProps> = ({ countyData = [], onCountyClick, selectedCounty, background = '#0f1419', labelColor = '#8b95a5' }) => {
  const getCD = (name: string) =>
    countyData.find(d => d.name.toUpperCase() === name.toUpperCase());

  const countyNames = Object.keys(KENYA_COUNTY_PATHS);

  return (
    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} style={{ width: '100%', height: '100%' }} xmlns="http://www.w3.org/2000/svg">
      <rect width={VIEW_W} height={VIEW_H} fill={background} />

      {countyNames.map(name => {
        const d = getCD(name);
        const isSelected = selectedCounty && selectedCounty.toUpperCase() === name.toUpperCase();
        const isDimmed = !!selectedCounty && !isSelected;

        const fill = isSelected ? '#ffffff' : isDimmed ? '#3a4250' : pct2color(d?.percentage ?? 0);
        const stroke = isSelected ? '#00D084' : background;
        const strokeWidth = isSelected ? 1.6 : 0.6;

        return (
          <path
            key={name}
            d={KENYA_COUNTY_PATHS[name]}
            fill={fill}
            fillOpacity={isDimmed ? 0.55 : 1}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
            style={{ cursor: onCountyClick ? 'pointer' : 'default', transition: 'fill 0.15s, fill-opacity 0.15s' }}
            onClick={() => onCountyClick && onCountyClick(d || { name, lat: 0, lng: 0, registered: 0, unregistered: 0, percentage: 0 })}
          >
            <title>
              {name}
              {d ? ` — ${Math.round(d.percentage)}% (${d.registered.toLocaleString()} of ${(d.registered + d.unregistered).toLocaleString()})` : ' — no data'}
            </title>
          </path>
        );
      })}

      {/* Legend */}
      <g transform="translate(6, 498)">
        {[{ c: '#00D084', l: '≥80%' }, { c: '#FFA500', l: '≥60%' }, { c: '#FF6B4A', l: '≥40%' }, { c: '#C41E3A', l: '<40%' }, { c: '#E8E6DE', l: 'no data' }]
          .map((item, i) => (
            <g key={item.l} transform={`translate(${i * 62}, 0)`}>
              <rect x={0} y={0} width={10} height={10} rx={2} fill={item.c} />
              <text x={13} y={9} fontSize="7.5" fill={labelColor}>{item.l}</text>
            </g>
          ))}
      </g>
    </svg>
  );
};
